import type Token from 'markdown-it/lib/token.mjs';

/**
 * Used to remove any extra open / close tokens that don't have matching counterparts.
 * Note that this assumes self-nesting is not supported as nodes would break on conversion otherwise if not supported.
 * This is for edge cases where the LLM might return something like panel_open panel_open panel_close where there is an extra
 * panel_open that is unaccounted for, which will break and lose any content between the first panel_open and the nearest panel_close.
 * Code would remove any unclosed panel_open tags when encountering another of the same type.
 *
 * This function returns a set of indices from the array of tokens to be removed.
 */
export function sanitizeCustomBlocks(tokens: Token[]): Set<number> {
	const openTokenIndexMap = new Map<string, number>();
	const toRemove = new Set<number>();

	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i];

		// This attribute is set earlier in this file (customElementToken.attrPush(['isCustomBlock', 'true']))
		// So we can identify tokens added by this rule
		if (token.attrGet('isCustomBlock') === 'true') {
			// We split by the last underscore
			const type = token.type.substring(0, token.type.lastIndexOf('_'));
			const prevIndex = openTokenIndexMap.get(type);

			// Only check for removal if type exists
			if (type && token.nesting === 1) {
				if (prevIndex !== undefined) {
					toRemove.add(prevIndex);
				}
				openTokenIndexMap.set(type, i);
			} else if (type && token.nesting === -1) {
				if (prevIndex !== undefined) {
					// If previous open token found, then we've found a match and we can close it
					openTokenIndexMap.delete(type);
				} else {
					toRemove.add(i);
				}
			}
		}
	}

	// Anything left in the stack is an unmatched open token
	openTokenIndexMap.forEach((remainingIndex) => toRemove.add(remainingIndex));

	return toRemove;
}
